/*
 * Boston Proper card-swatch hydrate.
 *
 * Why this exists: snippets/bp-product-card.liquid uses
 * all_products[sibling_handle] to look up each color's sibling product
 * for the swatch image + URL. Shopify caps all_products[] at 20 unique
 * products per Liquid render. On a PLP with ~24 cards × ~6 colors each
 * that cap is blown immediately — lookups after the limit silently
 * return nil (or the previously cached product), so every swatch on
 * later cards ends up pointing at the wrong sibling. The visible bug:
 * all 6 swatches on a card share the current product's style code, URL,
 * and price.
 *
 * The PDP color-siblings block (blocks/bp-color-siblings.liquid) hits
 * the same cap on its product page but only has 1 block per page, so
 * its all_products[] lookups stay within the limit. It also runs a JS
 * fallback per swatch that fetches /products/<handle>.js for the
 * featured_image — useful when Liquid couldn't resolve the sibling.
 *
 * This helper applies the same pattern to all cards on the page,
 * but goes a step further: instead of N fetches per card (one per
 * sibling), it fires one fetch per card to a dedicated alt-template
 * (/products/<handle>?view=hydrate, see templates/product.hydrate.liquid)
 * that emits the entire sibling list as JSON in a single round trip.
 * Each hydrate request runs in its own Liquid render context, so each
 * one is fresh against the 20-cap.
 *
 * Triggers lazily via IntersectionObserver so only cards near the
 * viewport fetch. Per-handle Promise cache dedupes parallel requests
 * across the page. Safe to load multiple times — re-entry is a no-op
 * after the singleton init.
 *
 * Public surface:
 *   window.BpCardHydrate.observeCards(rootEl?)  // observe new cards
 *   window.BpCardHydrate.hydrateHandle(handle)  // primitive — used by
 *                                                 bp-bloomreach.js
 */

(function () {
  'use strict';

  // Idempotent: if the script tag is included twice (e.g. once
  // globally + once per section), the second load is a no-op.
  if (window.BpCardHydrate) return;

  // Cap visible swatches at 3 to match the Liquid template
  // (snippets/bp-product-card.liquid:222 `swatch_limit = 3`) and
  // the Figma card spec — anything more than 3 collapses into the
  // '+N more' overflow chip.
  const SWATCH_LIMIT = 3;
  const cache = new Map();

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // CSS-safe color name: strip everything that isn't a letter. Used
  // as a final fallback when the hydrate doesn't return a thumb URL
  // (sibling missing bp_style metafield). Matches the filter drawer's
  // approach in bp-fonts.liquid.
  function colorToCss(name) {
    return String(name).toLowerCase().replace(/[^a-z]/g, '');
  }

  function handleFromUrl(url) {
    const m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  // Card-sized hover image from a style code — Amplience's set service by
  // INDEX (`_SET/2` = the 2nd real image), matching Atlas's PLP hover
  // (collection.js `_SET/2`). Was blind `/i/{style}_2`, but on accessories/
  // shoes/jewelry `_2` is the FIRST real image (there's no `_1`), so it
  // equalled the new `_SET/1` primary and the hover-swap showed no change.
  // `_SET/2` is always the second real image, so the hover swaps to a
  // genuinely different shot (QA 7/27).
  function hoverImageUrl(style) {
    const amp = (window.bpColorSiblings && window.bpColorSiblings.amplience) || '';
    return amp && style ? amp + '/s/bostonproper/' + style + '_SET/2?w=720' : '';
  }

  // Percent-encode a handle for the /products/<handle> path WITHOUT
  // double-encoding one that's already encoded. handleFromUrl() pulls the
  // handle straight out of an <a href>, so it arrives already percent-encoded
  // (e.g. a ® in the product name is `%C2%AE`). Running encodeURIComponent on
  // that re-encodes the `%` → `%25C2%25AE`, and the hydrate fetch 404s — so
  // Collective products whose names carry a ® / non-ASCII char (Hanky Panky
  // "BreatheSoft®…", "Signature Lace®…") never hydrated: no primary correction,
  // no hover/secondary image (QA 7/31). decodeURIComponent first makes this a
  // no-op for an already-encoded handle and still encodes a raw one, so it's
  // safe for both call sites (URL-derived handle, or a raw handle passed by
  // bp-bloomreach.js). Guard the decode so a stray lone `%` can't throw.
  function encodeHandle(handle) {
    let raw = String(handle);
    try { raw = decodeURIComponent(raw); } catch (e) { /* not encoded */ }
    return encodeURIComponent(raw);
  }

  function hydrateHandle(handle) {
    if (!handle) return Promise.resolve(null);
    if (cache.has(handle)) return cache.get(handle);
    const url = `/products/${encodeHandle(handle)}?view=hydrate`;
    const promise = fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    cache.set(handle, promise);
    return promise;
  }

  // Build the swatch <ul> body from a list of siblings. Bare <img>/<span>
  // (never wrapped in <a>) carrying the same data-bp-card-swatch-* /
  // data-bp-variant-url / data-bp-swatch-price attributes
  // snippets/bp-product-card.liquid renders statically — the global
  // click handler in snippets/bp-card-swatches.liquid owns the actual
  // image-swap + href-rewrite behavior from those attributes, so this
  // must match its contract exactly rather than navigate on click.
  function renderSwatchInner(siblings, productUrl, availableCount) {
    // The hydrate endpoint emits only in-stock colours with a swatch image;
    // filter defensively too so a sold-out swatch can never render.
    const avail = siblings.filter((s) => s.available !== false && s.thumb);
    const shown = avail.slice(0, SWATCH_LIMIT);
    const items = shown.map((s) => {
      const activeCls = s.is_current ? ' bp-card__swatch--active' : '';
      const current = s.is_current ? ' aria-current="true"' : '';
      // Attributes live on the <button> (a11y: keyboard-operable, named by
      // colour) for every sibling — including the current one — so the
      // global handler in bp-card-swatches.liquid resolves via closest().
      // Matches bp-product-card.liquid exactly.
      const attrs = `
                data-bp-card-swatch-image="${escapeHtml(s.card_image || '')}"
                data-bp-card-swatch-hover="${escapeHtml(s.hover_image || '')}"
                data-bp-variant-url="${escapeHtml(s.url || '')}"
                data-bp-swatch-price="${escapeHtml(s.price || '')}"
                ${s.compare_at_price ? `data-bp-swatch-compare="${escapeHtml(s.compare_at_price)}"` : ''}`;
      const inner = s.thumb
        ? `<img class="bp-card__swatch-img" src="${escapeHtml(s.thumb)}" alt="" aria-hidden="true" width="23" height="23" loading="lazy">`
        : `<span class="bp-card__swatch-img" style="background-color: ${escapeHtml(colorToCss(s.color))};" aria-hidden="true"></span>`;
      return `<li class="bp-card__swatch-item"><button type="button" class="bp-card__swatch${activeCls}" aria-label="${escapeHtml(s.color)}"${current}${attrs}>${inner}</button></li>`;
    }).join('');
    // +N links to the PDP (Shopbop pattern) — tap to see the remaining
    // colours. Count only IN-STOCK colours: prefer the endpoint's
    // available_count (total in-stock across the whole list), falling back
    // to the emitted in-stock count. Matches bp-product-card.liquid.
    const total = (typeof availableCount === 'number' && availableCount > avail.length)
      ? availableCount
      : avail.length;
    const extra = total - SWATCH_LIMIT;
    const overflow = extra > 0
      ? `<li class="bp-card__swatch-more"><a class="bp-card__swatch-more-link" href="${escapeHtml(productUrl || '#')}" aria-label="View all ${total} colours">+${extra}</a></li>`
      : '';
    return items + overflow;
  }

  // Collection eyebrow above the card title. BR's catalog feed carries
  // no collection attribute, so BR-rendered cards ship without one and
  // we fill it in from the hydrate payload. Liquid-rendered cards
  // already have the <p> from bp-product-card.liquid — never overwrite
  // it, since the section may have merchandised its own label.
  function applyCollection(article, collection) {
    if (!collection) return;
    // Sections with a "Show collection name on card" toggle opt out by
    // stamping data-bp-show-collection="0" on their root. An ABSENT
    // attribute means show — that matches the Liquid card, which only
    // hides on an explicit `show_collection: false`.
    if (article.closest('[data-bp-show-collection="0"]')) return;
    if (article.querySelector('.bp-card__collection')) return;
    const body = article.querySelector('.bp-card__body');
    if (!body) return;
    const el = document.createElement('p');
    el.className = 'bp-card__collection';
    el.textContent = collection;
    body.insertBefore(el, body.firstChild);
  }

  // Inject Horizon's quick-add (rendered server-side by the hydrate
  // endpoint) into a BR card so its bag button can open the BP quick-add
  // drawer — the same drawer the homepage carousel uses. The
  // .bp-card__quick-add wrapper is clipped to 1x1 / pointer-events:none by
  // the global CSS in bp-fonts.liquid; the visible bag (.bp-card__quick,
  // rendered by bp-bloomreach.js) is the trigger. Idempotent + opt-out
  // aware, and called before the hydrated-gate below so the bag's
  // hydrate-on-demand fallback can inject it on first click if a card is
  // clicked before it scrolls into view.
  function applyQuickAdd(article, html) {
    if (!html) return;
    if (article.dataset.bpCardQbStyle === 'disabled') return;
    if (article.querySelector('.bp-card__quick-add')) return;
    const media = article.querySelector('.bp-card__media');
    if (!media) return;
    const wrap = document.createElement('div');
    wrap.className = 'bp-card__quick-add';
    wrap.innerHTML = html;
    media.appendChild(wrap);
  }

  // Secondary/hover image (§9.4 bp_card_secondary_hover). BR cards render
  // with only their primary <img> (assets/bp-bloomreach.js renderCard —
  // BR's feed has no second image); this injects the
  // .bp-card__image--secondary the global hover-swap CSS in
  // snippets/bp-fonts.liquid already looks for (same selectors the
  // Liquid card / homepage carousel use), so no new CSS is needed.
  // Idempotent + opt-out aware: skips when the section turned the knob
  // off, or when a secondary image is already present (Liquid-rendered
  // cards already have theirs server-side).
  function applySecondaryImage(article, url) {
    if (!url) return;
    if (article.dataset.bpCardSecondaryHover === 'false') return;
    const link = article.querySelector('.bp-card__media-link');
    if (!link || link.querySelector('.bp-card__image--secondary')) return;
    const img = document.createElement('img');
    img.className = 'bp-card__image bp-card__image--secondary';
    img.src = url;
    img.alt = '';
    img.loading = 'lazy';
    img.setAttribute('aria-hidden', 'true');
    // The hook the swatch-click handler (snippets/bp-card-swatches.liquid)
    // resolves the hover image by — without it, changing the colour swatch
    // updated the primary image but left the hover image on the initial
    // colour's second shot (QA 7/27, Dragana). Matches the Liquid card
    // (snippets/bp-product-card.liquid), which sets it server-side.
    img.setAttribute('data-bp-card-image-secondary', '');
    link.appendChild(img);
  }

  // Primary/ghost image CORRECTION. BR's `thumb_image` is the only image its
  // feed carries, and for some Collective / non-Amplience products (e.g. Hanky
  // Panky) it points at a LATER Shopify shot (the 3rd image) instead of the
  // product's featured/first image, so bp-bloomreach.js renderCard paints the
  // wrong primary (it has no Amplience `_SET/1` to fall back on when bp_style is
  // blank). The hydrate endpoint sends `primary_image` (= product.featured_image,
  // the same View-1 the Liquid card + PDP show) ONLY for those Collective
  // products; house/Amplience cards get an empty value here so their correct
  // `_SET/1` primary is never touched. We must also replace `srcset` — the BR
  // primary carries a srcset built from the wrong thumb, and the browser would
  // otherwise keep picking a wrong-image candidate from it over our new `src`.
  function applyPrimaryImage(article, url, srcset) {
    if (!url) return;
    const img = article.querySelector('[data-bp-card-image-primary]');
    if (!img) return;
    // Idempotent: don't re-swap on a re-hydrate once we've corrected it.
    if (img.dataset.bpPrimaryFixed === '1') return;
    img.src = url;
    if (srcset) {
      img.setAttribute('srcset', srcset);
    } else {
      img.removeAttribute('srcset');
    }
    img.dataset.bpPrimaryFixed = '1';
  }

  // Red sale/promo line (discounts.discount_message). BR cards render no
  // promo, so inject it into the price row — matches the Liquid card
  // (snippets/bp-product-card.liquid) + the PDP (blocks/bp-promo-line).
  // Idempotent: skips if the card already has one (e.g. Liquid fallback card).
  function applyPromo(article, message) {
    if (!message) return;
    const row = article.querySelector('.bp-card__price-row');
    if (!row || row.querySelector('.bp-card__promo')) return;
    const span = document.createElement('span');
    span.className = 'bp-card__promo';
    span.textContent = message;
    row.appendChild(span);
  }

  function apply(article, data) {
    if (!article || !data) return;

    // Quick-add first — idempotent, and must run even when the card was
    // already swatch-hydrated (so a later force-hydrate can still inject it).
    applyQuickAdd(article, data.quick_add_html);
    applyPrimaryImage(article, data.primary_image, data.primary_srcset);
    applySecondaryImage(article, data.secondary_image);
    // Red sale/promo line — carried in the hydrate payload (same as
    // `collection`), so it renders on the perCardFallback path that actually
    // produces these cards when the Bloomreach batch path isn't used. Runs
    // before the hydrated guard so it applies even on a re-hydrate.
    applyPromo(article, data.promo);

    if (article.dataset.bpHydrated === '1') return;

    // Before the siblings guard — a product with no color siblings
    // still gets its eyebrow.
    applyCollection(article, data.collection);

    // Swatch row only for INV5/INV7 colour families (matches the PDP + Liquid
    // card). The hydrate endpoint reports isInv; non-family products drop any
    // initial BR-rendered swatches so no lone single-colour swatch shows.
    if (data.isInv === false) {
      const ulNon = article.querySelector('.bp-card__swatches');
      if (ulNon && ulNon.dataset.bpMockSwatches !== '1') ulNon.remove();
      article.dataset.bpHydrated = '1';
      return;
    }

    // Only render swatches for 2+ available colours (QA: Tamara) — a lone
    // swatch is dropped. Also remove any initial BR swatch row.
    if (!Array.isArray(data.siblings) || data.siblings.length <= 1) {
      const ulLone = article.querySelector('.bp-card__swatches');
      if (ulLone && ulLone.dataset.bpMockSwatches !== '1') ulLone.remove();
      article.dataset.bpHydrated = '1';
      return;
    }

    const swatchHtml = renderSwatchInner(data.siblings, article.dataset.productUrl || '', data.available_count);
    let ul = article.querySelector('.bp-card__swatches');

    // Design-preview mocks: when the mock <ul> is rendered (force-
    // decorations setting on), don't overwrite it. The mock is the
    // intended visual in preview mode. Flip the theme setting off
    // before production and the real swatches will hydrate normally.
    if (ul && ul.dataset.bpMockSwatches === '1') {
      article.dataset.bpHydrated = '1';
      return;
    }

    if (ul) {
      ul.innerHTML = swatchHtml;
    } else {
      // Liquid didn't render a UL at all (e.g. product had no
      // bp_stylecolorlist on the master). Insert one inside the body
      // so the hydrated swatches still appear.
      const body = article.querySelector('.bp-card__body');
      if (body) {
        const newUl = document.createElement('ul');
        newUl.className = 'bp-card__swatches';
        newUl.setAttribute('role', 'list');
        newUl.setAttribute('aria-label', 'Available colors');
        newUl.innerHTML = swatchHtml;
        body.appendChild(newUl);
      }
    }
    article.dataset.bpHydrated = '1';
  }

  // ---- Batched swatch path (BR cards) -------------------------------------
  // Cards that expose their colour family (data-bp-card-master +
  // data-bp-card-colors, emitted by bp-bloomreach.js renderCard) resolve ALL
  // their siblings' swatch data in ONE Storefront-GraphQL call
  // (window.bpColorSiblings.fetch) per observer flush — instead of one
  // /products/<handle>?view=hydrate round-trip per card. This is the PLP
  // mobile fan-out fix. Cards WITHOUT that data (Liquid cards, or a card whose
  // master couldn't be derived) fall back to the per-card path above. Quick-add
  // for BR cards is injected on demand on first bag tap (bp-collection.liquid
  // bindBagIcons), so it's intentionally not fetched here.

  const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  function money(n) {
    if (n == null || isNaN(n)) return '';
    return MONEY.format(Number(n)).replace(/\.00$/, '');
  }

  // Shopify-style handleize (matches bp-bloomreach.js deriveMaster).
  function handleize(s) {
    return String(s == null ? '' : s).toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  // Read a card's colour family → [{color, handle}] (sibling handle =
  // master + '-' + handleize(colour)). null when the card isn't batch-eligible.
  function siblingsForCard(article) {
    const master = article.dataset.bpCardMaster || '';
    const colorsRaw = article.dataset.bpCardColors || '';
    if (!master || !colorsRaw) return null;
    const seen = {};
    const out = [];
    colorsRaw.split(',').forEach((raw) => {
      const color = raw.trim();
      if (!color) return;
      const handle = master + '-' + handleize(color);
      if (seen[handle]) return;
      seen[handle] = 1;
      out.push({ color: color, handle: handle });
    });
    return out.length ? out : null;
  }

  // Build the swatch <ul> body from resolved (in-stock) siblings. Emits the
  // SAME DOM contract as renderSwatchInner / bp-product-card.liquid.
  function buildBatchSwatchHtml(resolved, productUrl) {
    const amp = (window.bpColorSiblings && window.bpColorSiblings.amplience) || '';
    const shown = resolved.slice(0, SWATCH_LIMIT);
    const items = shown.map((s) => {
      // Set service by index: `_SET/1` primary + `_SET/2` hover — always the
      // true 1st / 2nd real image, so accessories/shoes/jewelry (no `_1`) get
      // the right shot instead of the grey-bg composite / coming-soon (QA 7/27).
      const cardImg = amp && s.style ? amp + '/s/bostonproper/' + s.style + '_SET/1?w=720' : '';
      const hoverImg = amp && s.style ? amp + '/s/bostonproper/' + s.style + '_SET/2?w=720' : '';
      const priceStr = s.price != null ? money(s.price) : '';
      const compareStr = (s.compareAt != null && s.price != null && s.compareAt > s.price) ? money(s.compareAt) : '';
      const activeCls = s.isCurrent ? ' bp-card__swatch--active' : '';
      const current = s.isCurrent ? ' aria-current="true"' : '';
      const attrs =
        ` data-bp-card-swatch-image="${escapeHtml(cardImg)}"` +
        ` data-bp-card-swatch-hover="${escapeHtml(hoverImg)}"` +
        ` data-bp-variant-url="${escapeHtml('/products/' + s.handle)}"` +
        ` data-bp-swatch-price="${escapeHtml(priceStr)}"` +
        (compareStr ? ` data-bp-swatch-compare="${escapeHtml(compareStr)}"` : '');
      const img = `<img class="bp-card__swatch-img" src="${escapeHtml(s.swatchUrl || '')}" alt="" aria-hidden="true" width="23" height="23" loading="lazy">`;
      const pill = s.discount > 0 ? `<span class="bp-card__swatch-discount" aria-label="${s.discount}% off">-${s.discount}%</span>` : '';
      return `<li class="bp-card__swatch-item"><button type="button" class="bp-card__swatch${activeCls}" aria-label="${escapeHtml(s.color)}"${current}${attrs}>${img}</button>${pill}</li>`;
    }).join('');
    const extra = resolved.length - SWATCH_LIMIT;
    const overflow = extra > 0
      ? `<li class="bp-card__swatch-more"><a class="bp-card__swatch-more-link" href="${escapeHtml(productUrl || '#')}" aria-label="View all ${resolved.length} colours">+${extra}</a></li>`
      : '';
    return items + overflow;
  }

  function renderBatchSwatches(article, siblings, map) {
    if (article.dataset.bpHydrated === '1') return;
    const cardHandle = handleFromUrl(article.dataset.productUrl);

    // Secondary/hover image for the CARD'S OWN product — reuses the style
    // code this same batched GraphQL call already fetched for the swatch
    // row, so no extra request. Read directly off `map` (not the
    // in-stock-only `resolved` list below) so a card whose own entry gets
    // filtered out of the swatch list still gets its hover image.
    const selfInfo = map[cardHandle];
    if (selfInfo && selfInfo.style) {
      applySecondaryImage(article, hoverImageUrl(selfInfo.style));
    }
    // Red sale messaging for the card's own product (Semi-Annual Sale etc.).
    if (selfInfo && selfInfo.promo) applyPromo(article, selfInfo.promo);

    // Swatch row only for INV5/INV7 colour families — matches the PDP
    // (bp-color-siblings) + the Liquid card gate, so single-colour non-family
    // products don't show a lone swatch (QA: Tamara). Remove any initial
    // BR-rendered swatches and stop. Only when we KNOW it's non-INV
    // (selfInfo.isInv === false); unknown/undefined falls through (fail open).
    if (selfInfo && selfInfo.isInv === false) {
      const ulNon = article.querySelector('.bp-card__swatches');
      if (ulNon && ulNon.dataset.bpMockSwatches !== '1') ulNon.remove();
      article.dataset.bpHydrated = '1';
      return;
    }

    const resolved = [];
    siblings.forEach((sib) => {
      const info = map[sib.handle];
      if (!info || info.available !== true || !info.style) return; // in-stock + has style only
      resolved.push({
        color: sib.color, handle: sib.handle, style: info.style,
        swatchUrl: info.swatchUrl, price: info.price, compareAt: info.compareAt,
        discount: info.discount || 0, isCurrent: sib.handle === cardHandle
      });
    });
    // Only show swatches when there's a real choice — 2+ available colours.
    // A single available colour = no choice, so drop the lone swatch (QA:
    // Tamara). Remove any initial BR-rendered swatch row too.
    if (resolved.length <= 1) {
      const ulLone = article.querySelector('.bp-card__swatches');
      if (ulLone && ulLone.dataset.bpMockSwatches !== '1') ulLone.remove();
      article.dataset.bpHydrated = '1';
      return;
    }
    let ul = article.querySelector('.bp-card__swatches');
    if (ul && ul.dataset.bpMockSwatches === '1') { article.dataset.bpHydrated = '1'; return; }
    const html = buildBatchSwatchHtml(resolved, article.dataset.productUrl || '');
    if (ul) {
      ul.innerHTML = html;
    } else {
      const body = article.querySelector('.bp-card__body');
      if (body) {
        ul = document.createElement('ul');
        ul.className = 'bp-card__swatches';
        ul.setAttribute('role', 'list');
        ul.setAttribute('aria-label', 'Available colors');
        ul.innerHTML = html;
        body.appendChild(ul);
      }
    }
    article.dataset.bpHydrated = '1';
  }

  // Coalesce cards that intersect together into one GraphQL request per frame.
  let pendingBatch = [];
  let batchScheduled = false;
  function perCardFallback(article) {
    hydrateHandle(handleFromUrl(article.dataset.productUrl)).then((data) => apply(article, data));
  }
  function flushBatch() {
    batchScheduled = false;
    if (!pendingBatch.length) return;
    const batch = pendingBatch;
    pendingBatch = [];
    if (!(window.bpColorSiblings && typeof window.bpColorSiblings.fetch === 'function')) {
      batch.forEach((b) => perCardFallback(b.article));
      return;
    }
    const handles = [];
    const seen = {};
    batch.forEach((b) => b.siblings.forEach((s) => { if (!seen[s.handle]) { seen[s.handle] = 1; handles.push(s.handle); } }));
    window.bpColorSiblings.fetch(handles)
      .then((map) => { batch.forEach((b) => renderBatchSwatches(b.article, b.siblings, map || {})); })
      .catch(() => { batch.forEach((b) => perCardFallback(b.article)); }); // GraphQL down → per-card
  }
  function queueBatch(article, siblings) {
    pendingBatch.push({ article: article, siblings: siblings });
    if (batchScheduled) return;
    batchScheduled = true;
    if ('requestAnimationFrame' in window) window.requestAnimationFrame(flushBatch);
    else setTimeout(flushBatch, 0);
  }

  // Single shared observer for the whole page — cheaper than one per
  // card. 300px rootMargin gives a head start before the card is
  // actually visible so the swap doesn't flash during scroll.
  const observer = ('IntersectionObserver' in window) ? new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const article = entry.target;
      obs.unobserve(article);
      const siblings = siblingsForCard(article);
      if (siblings && window.bpColorSiblings && typeof window.bpColorSiblings.fetch === 'function') {
        queueBatch(article, siblings);        // BR card → batched GraphQL swatches
      } else {
        perCardFallback(article);             // Liquid card / no family data → per-card hydrate
      }
    });
  }, { rootMargin: '300px 0px' }) : null;

  function observeCards(root) {
    if (!observer) return;
    const scope = root || document;
    scope.querySelectorAll('.bp-card[data-product-url]:not([data-bp-hydrated="1"])').forEach((article) => {
      observer.observe(article);
    });
  }

  // Public API.
  window.BpCardHydrate = {
    observeCards,
    hydrateHandle,
    // Exposed for bp-bloomreach.js to upgrade its visual-only swatches
    // without duplicating the render code.
    applyHydration: apply
  };

  // Initial pass once the DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observeCards(document));
  } else {
    observeCards(document);
  }
})();
